import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Switch,
  FormControlLabel, Box, MenuItem,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface Agency {
  id: number;
  tenant_id: number;
  name: string;
  code: string;
  agency_type: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  is_active: boolean;
  notes: string | null;
}

const AGENCY_TYPES = [
  { value: 'fire', label: 'Fire Department' },
  { value: 'ems', label: 'EMS / Ambulance' },
  { value: 'rescue', label: 'Rescue' },
  { value: 'law', label: 'Law Enforcement' },
  { value: 'other', label: 'Other' },
];

export default function AgenciesPage() {
  const { tenantId } = useAuth();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Agency | null>(null);
  const [form, setForm] = useState({
    name: '', code: '', agency_type: 'fire',
    contact_name: '', contact_email: '', contact_phone: '',
    is_active: true, notes: '',
  });

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/tenants/${tenantId}/agencies`);
      setAgencies(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load agencies');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '', code: '', agency_type: 'fire',
      contact_name: '', contact_email: '', contact_phone: '',
      is_active: true, notes: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (a: Agency) => {
    setEditing(a);
    setForm({
      name: a.name, code: a.code, agency_type: a.agency_type,
      contact_name: a.contact_name || '', contact_email: a.contact_email || '',
      contact_phone: a.contact_phone || '',
      is_active: a.is_active, notes: a.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!tenantId) return;
    try {
      if (editing) {
        await api.put(`/api/tenants/${tenantId}/agencies/${editing.id}`, form);
      } else {
        await api.post(`/api/tenants/${tenantId}/agencies`, form);
      }
      setDialogOpen(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id: number) => {
    if (!tenantId || !window.confirm('Delete this agency? All stations and units assigned to it will become unassigned.')) return;
    try {
      await api.delete(`/api/tenants/${tenantId}/agencies/${id}`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  const typeLabel = (t: string) => AGENCY_TYPES.find(at => at.value === t)?.label || t;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Agencies</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Agency</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center">Loading...</TableCell></TableRow>
            ) : agencies.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">No agencies configured</TableCell></TableRow>
            ) : agencies.map(a => (
              <TableRow key={a.id}>
                <TableCell>{a.name}</TableCell>
                <TableCell><code>{a.code}</code></TableCell>
                <TableCell>{typeLabel(a.agency_type)}</TableCell>
                <TableCell>
                  {a.contact_name && <div>{a.contact_name}</div>}
                  {a.contact_email && <div style={{ fontSize: '0.85em', opacity: 0.7 }}>{a.contact_email}</div>}
                </TableCell>
                <TableCell>{a.is_active ? 'Yes' : 'No'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(a)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(a.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Agency' : 'Add Agency'}</DialogTitle>
        <DialogContent>
          <TextField label="Agency Name" fullWidth margin="dense" required
            value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <TextField label="Agency Code" fullWidth margin="dense" required
            value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
            helperText="Short identifier (e.g., CFD, BCEMS)" />
          <TextField label="Type" fullWidth margin="dense" select
            value={form.agency_type} onChange={e => setForm({ ...form, agency_type: e.target.value })}>
            {AGENCY_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <TextField label="Contact Name" fullWidth margin="dense"
            value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} />
          <TextField label="Contact Email" fullWidth margin="dense"
            value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} />
          <TextField label="Contact Phone" fullWidth margin="dense"
            value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} />
          <TextField label="Notes" fullWidth margin="dense" multiline rows={2}
            value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          {editing && (
            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />}
              label="Active"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name || !form.code}>
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
